import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { Quote } from '@/types/v2/quote';
import axios from '@/lib/v2/axios';
import ProfessionalDialog from '@/components/common/professional-dialog';

const schema = zod.object({
  community_id: zod.coerce.string(),
  request_id: zod.coerce.string(),
  total: zod.coerce.number({ invalid_type_error: 'Enter a valid amount' }).positive('Amount must be greater than $0'),
  description: zod.string().min(1, 'Description is required'),
});

type FormValues = zod.infer<typeof schema>;

interface CreateUpdateQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  requestId: string;
  requestNumericId?: number;
  communityId: string;
  quote: Quote | null;
}

const CreateUpdateQuoteDialog: FC<CreateUpdateQuoteDialogProps> = ({
  open,
  onClose,
  onRefresh,
  requestId,
  requestNumericId,
  communityId,
  quote,
}) => {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      community_id: String(communityId),
      request_id: String(requestId),
      total: 0 as unknown as number,
      description: '',
    },
  });

  console.log('errors', errors);

  // Sync form when dialog opens or quote changes
  useEffect(() => {
    if (open) {
      setSubmitError(null);
      reset(
        quote
          ? { community_id: String(communityId), request_id: String(requestId), total: parseFloat(quote.total) as unknown as number, description: quote.description }
          : { community_id: String(communityId), request_id: String(requestId), total: 0 as unknown as number, description: '' }
      );
    }
  }, [open, communityId, requestId, quote?.id, reset]);

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const communityIdNum = Number(communityId);
      const requestIdNum = requestNumericId ?? Number(requestId);
      if (Number.isNaN(communityIdNum) || Number.isNaN(requestIdNum)) {
        throw new Error('Invalid community or request');
      }
      const response = await axios.post(
        `/api/v2/communities/${communityId}/requests/${requestId}/quotes`,
        {
          community_id: communityIdNum,
          request_id: requestIdNum,
          total: String(data.total),
          description: data.description,
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message ?? 'Quote saved successfully');
      queryClient.invalidateQueries({ queryKey: ['request-quotes', requestId] });
      onRefresh?.();
      onClose();
      reset();
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setSubmitError(msg ?? 'Failed to save quote. Please try again.');
    },
  });

  const onSubmit = useCallback(
    (data: FormValues) => {
      setSubmitError(null);
      mutation.mutate(data);
    },
    [mutation]
  );

  const handleClose = useCallback(() => {
    if (mutation.isPending) return;
    setSubmitError(null);
    onClose();
    reset();
  }, [mutation.isPending, onClose, reset]);

  const isPending = isSubmitting || mutation.isPending;

  return (
    <ProfessionalDialog
      actions={
        <Stack direction="row" spacing={1}>
          <Button disabled={isPending} onClick={handleClose} size="small" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            loading={isPending}
            onClick={() => { handleSubmit(onSubmit)(); }}
            size="small"
            variant="contained"
          >
            {quote ? 'Update Quote' : 'Add Quote'}
          </LoadingButton>
        </Stack>
      }
      actionsSx={{ py: 1.5 }}
      autoHeight
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle="Provide the details for your quote"
      title={quote ? 'Update Quote' : 'Create Quote'}
    >
      <Stack spacing={3} sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.description)}
                  fullWidth
                  helperText={errors.description?.message}
                  label="Description"
                  multiline
                  placeholder="Describe the work or service being quoted..."
                  rows={4}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </Grid>

          <Grid md={6} xs={12}>
            <Controller
              control={control}
              name="total"
              render={({ field: { onChange, value, ...rest } }) => (
                <TextField
                  {...rest}
                  error={Boolean(errors.total)}
                  fullWidth
                  helperText={errors.total?.message ?? 'e.g. 1250.00'}
                  inputProps={{ inputMode: 'decimal', min: 0.01, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  label="Total amount *"
                  onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Enter amount"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  type="number"
                  value={value === 0 ? '' : value}
                />
              )}
            />
          </Grid>
        </Grid>

        {submitError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">{submitError}</Typography>
          </Alert>
        )}
      </Stack>
    </ProfessionalDialog>
  );
};

export default CreateUpdateQuoteDialog;
